import './Duel.css';
import { useId } from 'preact/hooks';
import { AnimatedNumber } from '../../components/AnimatedNumber';
import { DualBar } from '../../components/DualBar';
import { PartyTag } from '../../components/PartyTag';
import { Portrait } from '../../components/Portrait';
import { t } from '../../copy/strings';
import { fmtPct, fmtPctParts, pctDecimals } from '../../format/numbers';
import type { Candidate, CountStatus, Decision } from '../../model/types';
import { openProfile } from '../../state/actions';
import { useConfig, useLayout } from '../../state/hooks';
import { partyClass, partyOf } from '../../theme/parties';
import { type FirstRoundEntry, FirstRoundRef } from '../hero/FirstRoundRef';
import { MarginRows } from '../hero/MarginRows';

/** O que o duelo lê de `view.governor[uf]` (`PlaceView & { candidates }` satisfaz esta forma, §8.3 bloco 3). */
export interface DuelRace {
  candidates: readonly [Candidate, Candidate];
  shares: readonly [number, number];
  votes: readonly [number, number];
  validVotes: number;
  /** Eleitores (não votos) em seções não totalizadas. */
  remainingElectorate: number;
  status: CountStatus;
  mathDecision: Decision | null;
  tseResult: Decision | null;
}

export interface DuelProps {
  race: DuelRace;
  /** `FirstRoundResults.governor[uf]` na forma do `FirstRoundRef` (variante `compact`); null = sem a linha. */
  firstRound?: readonly [FirstRoundEntry, FirstRoundEntry] | null;
  /** Antes das 17h00 (E01): só os nomes, qualquer que seja o payload (§8.2 bloco 4). */
  pre?: boolean;
}

export interface DuelSide {
  candidate: Candidate;
  share: number;
  votes: number;
}

/**
 * Lados na ordem de NÚMERO (N01, N16): número menor à esquerda, qualquer que seja o líder (AC-8.3-01). O contrato já
 * entrega o par nessa ordem (§14.3); se vier invertido, o duelo o reordena com fatias e votos juntos.
 */
export function duelSides(race: DuelRace): [DuelSide, DuelSide] {
  const a: DuelSide = { candidate: race.candidates[0], share: race.shares[0], votes: race.votes[0] };
  const b: DuelSide = { candidate: race.candidates[1], share: race.shares[1], votes: race.votes[1] };
  return Number(a.candidate.number) > Number(b.candidate.number) ? [b, a] : [a, b];
}

/** Sem votos válidos, ou antes das 17h00: o duelo não é desenhado e dá lugar aos dois nomes (§8.3 bloco 4). */
export function duelIsEmpty(race: Pick<DuelRace, 'validVotes'>, pre = false): boolean {
  return pre || !(race.validVotes > 0);
}

/**
 * Duelo de governador (§8.3): os dois lado a lado (número menor à esquerda), percentuais Faustina na cor do partido (1
 * casa; 2 quando os dois estão a menos de 1 ponto), `DualBar` fina com a marca de 50%, `FirstRoundRef` compacta e
 * `MarginRows` em células. Mesmas classes para os dois lados (só `party-{n}` muda); o espelho da direita é por posição.
 * Cada lado é um `<button>` cujo nome é o conteúdo visível, com `hero.leader.aria` em `aria-describedby` (§15.4.2);
 * na TV, só leitura.
 */
export function Duel({ race, firstRound = null, pre = false }: DuelProps) {
  const photos = useConfig().features.photos;
  const tv = useLayout().mode === 'tv';
  const id = useId();
  const sides = duelSides(race);

  const describe = (c: Candidate) =>
    t('hero.leader.aria', { NOME: c.display_name, SIGLA: partyOf(c).display || c.party_code, NUM: c.number });
  const open = (c: Candidate) => openProfile(c.number, 'governor', 'list');

  if (duelIsEmpty(race, pre)) {
    return (
      <div class="duel" data-testid="duel" data-state="empty">
        <ul class="duel__names">
          {sides.map(({ candidate: c }, i) => {
            const body = (
              <>
                <Portrait candidate={c} photo={photos ? c.photo : null} size={26} />
                <span class="duel__names-name">{c.display_name}</span>
                <PartyTag candidate={c} size="sm" />
              </>
            );
            const descId = `${id}-n${i}`;
            return (
              <li key={i} class={`duel__names-item ${partyClass(partyOf(c))}`} data-number={c.number}>
                {tv ? (
                  <span class="duel__names-cell">{body}</span>
                ) : (
                  <button type="button" class="duel__names-cell" aria-describedby={descId} onClick={() => open(c)}>
                    {body}
                    <span id={descId} hidden>
                      {describe(c)}
                    </span>
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  const decision = race.tseResult ?? race.mathDecision;
  const decimals = pctDecimals(sides[0].share, sides[1].share);
  const pctValue = (v: number): string => fmtPctParts(v, decimals).value;
  const pctSr = (v: number): string => t('hero.leader.share.sr', { P: fmtPct(v, decimals) });
  const [a, b] = sides;
  const label = t('duel.aria', {
    A: a.candidate.display_name,
    pA: fmtPct(a.share, decimals),
    B: b.candidate.display_name,
    pB: fmtPct(b.share, decimals),
  });

  return (
    <div class="duel" data-testid="duel" data-state={decision ? 'decided' : 'counting'}>
      <div class="duel__sides">
        {sides.map((side, i) => {
          const c = side.candidate;
          const party = partyOf(c);
          const descId = `${id}-d${i}`;
          const body = (
            <>
              <span class="duel__who">
                <Portrait candidate={c} photo={photos ? c.photo : null} size={tv ? 64 : 40} />
                <span class="duel__id">
                  <span class="duel__name">{c.display_name}</span>
                  <PartyTag candidate={c} size="sm" />
                </span>
              </span>
              <span class="duel__fig" data-dataviz="">
                <AnimatedNumber value={side.share} format={pctValue} srFormat={pctSr} class="duel__pct" />
                <sup class="duel__unit" aria-hidden="true">
                  {fmtPctParts(side.share, decimals).unit}
                </sup>
              </span>
            </>
          );
          const cls = `duel__side ${partyClass(party)}`;
          if (tv) {
            return (
              <div key={i} class={cls} data-number={c.number}>
                {body}
              </div>
            );
          }
          return (
            <button
              key={i}
              type="button"
              class={cls}
              data-number={c.number}
              aria-describedby={descId}
              onClick={() => open(c)}
            >
              {body}
              <span id={descId} hidden>
                {describe(c)}
              </span>
            </button>
          );
        })}
      </div>
      <DualBar
        shares={[a.share, b.share]}
        parties={[partyOf(a.candidate), partyOf(b.candidate)]}
        names={[a.candidate.display_name, b.candidate.display_name]}
        decided={decision !== null}
        variant="thin"
        label={label}
      />
      <FirstRoundRef entries={firstRound} compact />
      <MarginRows
        marginPts={Math.abs(b.share - a.share) * 100}
        diffVotes={Math.abs(b.votes - a.votes)}
        remainingElectorate={race.remainingElectorate}
        complete={race.status === 'complete'}
        decided={decision !== null}
        variant="cells"
      />
    </div>
  );
}
